import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Wrench, Clock, Mail, ChevronRight, Zap, Shield, Star } from 'lucide-react';

const LandingPage = () => {

  const features = [
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Prenotazione Facile",
      description: "Prenota il tagliando della tua NIU in pochi click, scegliendo data e ora."
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Tecnici Specializzati",
      description: "Il nostro team è certificato NIU per garantire la massima qualità."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Tempi Rapidi",
      description: "Tagliando completato in giornata, ritira la tua moto quando vuoi."
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Aggiornamenti Email",
      description: "Ricevi notifiche per ogni fase del tagliando."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Zap className="w-8 h-8 text-primary" />
              <span className="font-heading font-black text-xl">NIU Service</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#come-funziona" className="text-muted-foreground hover:text-foreground transition-colors">Come Funziona</a>
              <Link to="/shop/login" className="text-muted-foreground hover:text-foreground transition-colors">Area Negozio</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" data-testid="login-btn">Accedi</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="register-btn">
                  Registrati
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <Zap className="w-4 h-4" />
                <span>Servizio Autorizzato NIU</span>
              </div>
              <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter leading-none">
                Tagliando<br />
                <span className="text-primary">Moto NIU</span><br />
                Semplice e Veloce
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg">
                Prenota il tagliando della tua moto elettrica NIU online. 
                Servizio professionale e veloce per garantire qualità e attenzione.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto h-14 px-8 text-lg font-bold" data-testid="hero-register-btn">
                    Prenota Ora
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#come-funziona">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto h-14 px-8 text-lg font-bold border-2" data-testid="hero-learn-more-btn">
                    Scopri di Più
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative animate-slide-up">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_192577ce-7fdf-46f9-8a87-b0db7a614625/artifacts/j6tsg8dw_NQIX500.jpg" 
                  alt="NIU Electric Scooter"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-md rounded-lg p-4">
                      <p className="text-white text-sm font-medium">Tempo Medio</p>
                      <p className="text-white text-3xl font-black">2h</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="come-funziona" className="py-20 md:py-32 bg-muted/30">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Come Funziona
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Prenota il tagliando in 4 semplici passaggi
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Registrati", desc: "Crea il tuo account inserendo i dati del veicolo" },
              { step: "02", title: "Prenota", desc: "Scegli data e ora per la consegna e il ritiro" },
              { step: "03", title: "Attendi Conferma", desc: "Il negozio confermerà il tagliando via email" },
              { step: "04", title: "Ritira", desc: "Ricevi una mail quando il veicolo è pronto" }
            ].map((item, idx) => (
              <Card key={idx} className="relative overflow-hidden border-border/50 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <span className="text-7xl font-black text-primary/10 absolute -top-4 -right-2">
                    {item.step}
                  </span>
                  <div className="relative">
                    <h3 className="font-heading text-2xl font-bold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="border-border/50 hover:border-primary/50 transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    {feature.icon}
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <Card className="bg-primary text-primary-foreground overflow-hidden">
            <CardContent className="p-8 md:p-16">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
                    Pronto per Prenotare?
                  </h2>
                  <p className="text-primary-foreground/80 text-lg mb-6">
                    Registrati ora e prenota il tagliando della tua NIU. 
                    Riceverai conferma via email.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link to="/register">
                      <Button size="lg" variant="secondary" className="w-full sm:w-auto h-14 px-8 text-lg font-bold" data-testid="cta-register-btn">
                        Registrati Gratis
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="flex justify-center gap-4">
                  <div className="text-center">
                    <Shield className="w-12 h-12 mx-auto mb-2 opacity-80" />
                    <p className="text-sm opacity-80">Sicuro</p>
                  </div>
                  <div className="text-center">
                    <Star className="w-12 h-12 mx-auto mb-2 opacity-80" />
                    <p className="text-sm opacity-80">Qualità</p>
                  </div>
                  <div className="text-center">
                    <Zap className="w-12 h-12 mx-auto mb-2 opacity-80" />
                    <p className="text-sm opacity-80">Veloce</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Zap className="w-6 h-6 text-primary" />
              <span className="font-heading font-bold">NIU Service Italia</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/shop/login" className="hover:text-foreground transition-colors">Area Negozio</Link>
              <span>© 2024 NIU Service</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
